import * as React from 'react';

import { DropdownButton, DropdownButtonType } from '@tableau/widgets';

import { ExplorerToolbarTestIds } from '../Explorer/ExplorerToolbar';
import { ExplorerDropdownIconButton } from './ExplorerDropdownIconButton';
import { ExplorerFlyoutPanelProps } from './ExplorerFlyoutPanel';

export interface ExplorerDropdownProps {
  readonly buttonTitle?: string;
  readonly panelTitle: string;
  readonly testId: string;
  readonly renderPanel: (props: ExplorerFlyoutPanelProps) => JSX.Element;
  readonly renderButtonContent: (showingOrActive: boolean, disabled: boolean) => JSX.Element;
  readonly buttonStyleOverrides?: React.CSSProperties;
  readonly disabled?: boolean;
  readonly renderButtonContentOnly?: boolean;
}

export interface ExplorerDropdownState {
  showingPanel: boolean;
}

export class ExplorerDropdown extends React.Component<ExplorerDropdownProps, ExplorerDropdownState> {
  private buttonElement: HTMLElement | null = null;
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();

  public constructor(props: ExplorerDropdownProps) {
    super(props);

    this.state = { showingPanel: false };
  }

  private setButtonRef = (ref: HTMLButtonElement | null): void => {
    this.buttonElement = ref;
  }

  protected togglePanelVisibility = (): void => {
    this.setState((state) => {
      return { showingPanel: !state.showingPanel };
    });
  }

  protected handleClick = (e: React.SyntheticEvent<HTMLDivElement, Event>): void => {
    this.togglePanelVisibility();
  }

  protected handleDoubleClick = (e: React.SyntheticEvent<HTMLDivElement, Event>): void => { };

  private hidePanel = (): void => {
    if (this.dropdownButtonRef.current) {
      this.dropdownButtonRef.current.toggle(false);
    }
    this.setState({ showingPanel: false });
  }

  private shouldRenderPanel = (): boolean => {
    return this.state.showingPanel;
  }

  private renderPanel = (): JSX.Element => {
    const panelProps: ExplorerFlyoutPanelProps = {
      anchorEl: this.buttonElement,
      testId: this.props.testId,
      title: this.props.panelTitle,
      onCloseRequest: this.hidePanel
    };

    return this.props.renderPanel(panelProps);
  }

  public render(): JSX.Element {
    if (!this.props.renderButtonContentOnly) {
      return <>
        <DropdownButton
          aria-haspopup={'menu'}
          buttonRef={this.setButtonRef}
          aria-controls={`${this.props.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`}
          label={''}
          title={this.props.buttonTitle}
          testId={`${this.props.testId}-Button`}
          ref={this.dropdownButtonRef}
          disabled={this.props.disabled}
          onOpen={() => this.setState({ showingPanel: true })}
          onClose={() => this.setState({ showingPanel: false })}
          type={DropdownButtonType.Line}
        >
          {this.props.renderButtonContent(false, this.props.disabled)}
        </DropdownButton>
        {this.shouldRenderPanel() && this.renderPanel()}
      </>;
    } else {
      return (
        <div onDoubleClick={this.handleDoubleClick}>
          <ExplorerDropdownIconButton
            setButtonRef={this.setButtonRef}
            testId={this.props.testId}
            onClick={this.handleClick}
            title={this.props.buttonTitle}
            renderButtonContent={this.props.renderButtonContent}
            showingPanel={this.shouldRenderPanel()}
            buttonStyleOverrides={this.props.buttonStyleOverrides}
            disabled={this.props.disabled}
          />
          {this.shouldRenderPanel() && this.renderPanel()}
        </div>
      );
    }
  }
}
